import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Title, Text, List, Divider, Button, Group, Stack } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import AuthLogo from '@components/auth-logo';

function PrivacyContent() {
  return (
    <Stack gap="lg">
      <Text c="dimmed" size="sm">
        Effective Date: To be announced
      </Text>

      <Text>
        VoyLogic ("we," "us," or "our") is a travel discovery and itinerary planning platform designed for use by travel
        advisors and travel professionals. This Privacy Policy explains how we collect, use, and protect information
        when you use our platform.
      </Text>

      <Divider />

      <Title order={3}>1. Information We Collect</Title>

      <Title order={4}>A. Information You Provide</Title>
      <List>
        <List.Item>Name, email, and account credentials</List.Item>
        <List.Item>Business or agency affiliation</List.Item>
        <List.Item>Travel inputs such as destinations, preferences, and selected activities</List.Item>
        <List.Item>Itinerary content you create or modify</List.Item>
      </List>

      <Title order={4}>B. Content You Generate</Title>
      <Text>
        VoyLogic processes the inputs you provide to generate ranked destinations and regions, attractions and
        activities, and draft itineraries. This content is stored and used to operate and improve the platform.
      </Text>

      <Title order={4}>C. Automatically Collected Information</Title>
      <List>
        <List.Item>Device and browser type</List.Item>
        <List.Item>IP address and approximate location</List.Item>
        <List.Item>Usage data (features used, session activity)</List.Item>
      </List>

      <Title order={4}>D. Third-Party Data Sources</Title>
      <Text>
        VoyLogic incorporates data from third-party providers, including Google (e.g., Maps and Places data), to support
        mapping, location, and discovery features.
      </Text>

      <Divider />

      <Title order={3}>2. How We Use Information</Title>
      <List>
        <List.Item>Provide and operate the VoyLogic platform</List.Item>
        <List.Item>Generate travel recommendations and draft itineraries</List.Item>
        <List.Item>Improve platform functionality and performance</List.Item>
        <List.Item>Communicate with users regarding accounts and support</List.Item>
      </List>
      <Text>We do not use your data to market directly to your clients.</Text>

      <Divider />

      <Title order={3}>3. Data Ownership and Role</Title>
      <Text>VoyLogic is a tool used by travel professionals.</Text>
      <List>
        <List.Item>You (the travel advisor) control your client relationships</List.Item>
        <List.Item>You are responsible for how you use and share any client-related data</List.Item>
        <List.Item>VoyLogic processes data on your behalf to generate outputs</List.Item>
      </List>

      <Divider />

      <Title order={3}>4. Data Sharing</Title>
      <Text>
        We do not sell personal data. We may share data with service providers (hosting, analytics, and infrastructure),
        third-party integrations you choose to connect, or as required by law.
      </Text>

      <Divider />

      <Title order={3}>5. Cookies and Tracking</Title>
      <Text>
        We use cookies to enable core platform functionality and analyze usage and performance. You can manage cookies
        through your browser settings.
      </Text>

      <Divider />

      <Title order={3}>6. Data Retention</Title>
      <Text>
        We retain data while your account is active, as necessary to provide services, and as required for legal or
        operational purposes.
      </Text>

      <Divider />

      <Title order={3}>7. Data Security</Title>
      <Text>We implement reasonable safeguards to protect your data. However, no system is completely secure.</Text>

      <Divider />

      <Title order={3}>8. Your Rights</Title>
      <Text>You may request to access, correct, or delete your data. Contact us at the address below.</Text>

      <Divider />

      <Title order={3}>9. International Use</Title>
      <Text>VoyLogic operates globally, and data may be processed in the United States or other jurisdictions.</Text>

      <Divider />

      <Title order={3}>10. Children's Privacy</Title>
      <Text>VoyLogic is not intended for individuals under 18.</Text>

      <Divider />

      <Title order={3}>11. Changes to This Policy</Title>
      <Text>We may update this policy from time to time. Updates will be posted with a revised effective date.</Text>

      <Divider />

      <Title order={3}>12. Contact</Title>
      <Text>Global Thematic Insights LLC</Text>
    </Stack>
  );
}

function TermsContent() {
  return (
    <Stack gap="lg">
      <Text c="dimmed" size="sm">
        Effective Date: To be announced
      </Text>

      <Text>
        These Terms of Use ("Terms") govern your access to and use of the VoyLogic platform ("Service") provided by
        Global Thematic Insights LLC ("VoyLogic," "we," "us," or "our"). By using the Service, you agree to these Terms.
      </Text>

      <Divider />

      <Title order={3}>1. Description of the Service</Title>
      <Text>
        VoyLogic provides a travel discovery and itinerary planning tool designed primarily for travel advisors and
        travel professionals. The Service enables users to explore destinations and regions, identify attractions and
        activities, and generate draft itineraries based on user inputs. VoyLogic is a software tool only and does not
        provide travel services.
      </Text>

      <Divider />

      <Title order={3}>2. Eligibility and Accounts</Title>
      <List>
        <List.Item>Be at least 18 years old</List.Item>
        <List.Item>Provide accurate account information</List.Item>
        <List.Item>Maintain the security of your login credentials</List.Item>
      </List>
      <Text>You are responsible for all activity under your account.</Text>

      <Divider />

      <Title order={3}>3. Professional Use and Responsibility</Title>
      <Text>
        VoyLogic is intended for use by travel professionals. You acknowledge that you are responsible for reviewing,
        validating, and modifying all outputs; your client relationships and communications; and how you use outputs in
        your business. VoyLogic does not interact directly with your clients.
      </Text>

      <Divider />

      <Title order={3}>4. Nature of Outputs</Title>
      <Text>
        VoyLogic generates draft, informational outputs based on user inputs and available data, including destination
        recommendations, attractions and activities, and suggested itineraries. Outputs may be incomplete, approximate,
        or subject to change. Independent verification is required before use.
      </Text>

      <Divider />

      <Title order={3}>5. No Travel Services or Booking</Title>
      <Text>
        VoyLogic does not book travel, provide transportation, accommodation, or tours, or guarantee availability,
        pricing, or suitability. Any travel arrangements are made solely by you or third parties.
      </Text>

      <Divider />

      <Title order={3}>6. Third-Party Data and Content</Title>
      <Text>
        The Service may incorporate data from third-party providers, including Google (e.g., Maps and Places data).
        VoyLogic does not control third-party data and does not guarantee its accuracy or completeness.
      </Text>

      <Divider />

      <Title order={3}>7. Integrations and Third-Party Tools</Title>
      <Text>
        VoyLogic may allow integration with third-party platforms. If you use integrations, data may be transferred to
        those platforms and their terms and privacy policies will apply. VoyLogic is not responsible for third-party
        services.
      </Text>

      <Divider />

      <Title order={3}>8. Acceptable Use</Title>
      <Text>
        You agree not to use the Service for unlawful purposes, reverse engineer or scrape the platform, interfere with
        system integrity or performance, or upload malicious or harmful content.
      </Text>

      <Divider />

      <Title order={3}>9. Intellectual Property</Title>
      <Text>
        VoyLogic retains all rights to the platform, underlying technology, and algorithms. You retain rights to your
        input data and client information. You grant VoyLogic a limited license to process your inputs to operate the
        Service.
      </Text>

      <Divider />

      <Title order={3}>10. Fees and Subscription</Title>
      <Text>
        Subscription fees will be disclosed at the time of purchase. Fees are non-refundable unless otherwise stated. We
        may modify pricing with notice.
      </Text>

      <Divider />

      <Title order={3}>11. Disclaimer of Warranties</Title>
      <Text>
        The Service is provided "as is" and "as available." VoyLogic makes no warranties regarding accuracy of outputs,
        availability of destinations or services, or fitness for a particular purpose.
      </Text>

      <Divider />

      <Title order={3}>12. Limitation of Liability</Title>
      <Text>
        To the maximum extent permitted by law, VoyLogic shall not be liable for errors or omissions in generated
        outputs, travel decisions made using the Service, loss of business, revenue, or clients, or indirect,
        incidental, or consequential damages. Total liability shall not exceed the amount paid by you in the preceding
        12 months.
      </Text>

      <Divider />

      <Title order={3}>13. Indemnification</Title>
      <Text>
        You agree to indemnify and hold VoyLogic harmless from claims arising from your use of the Service, your
        interactions with clients, or your use of generated outputs.
      </Text>

      <Divider />

      <Title order={3}>14. Termination</Title>
      <Text>
        We may suspend or terminate your access if you violate these Terms or misuse the Service. You may terminate your
        account at any time.
      </Text>

      <Divider />

      <Title order={3}>15. Changes to the Service or Terms</Title>
      <Text>We may modify the Service or these Terms. Continued use constitutes acceptance of updates.</Text>

      <Divider />

      <Title order={3}>16. Governing Law</Title>
      <Text>These Terms are governed by the laws of the United States.</Text>

      <Divider />

      <Title order={3}>17. Contact</Title>
      <Text>Global Thematic Insights LLC</Text>
    </Stack>
  );
}

function LegalPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isPrivacy = pathname === '/privacy';

  return (
    <Container size="md" my={40} pb={60}>
      <AuthLogo />

      <Group mt="xl" mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)} size="sm">
          Back
        </Button>
      </Group>

      <Title order={2} mb="xs">
        {isPrivacy ? 'Privacy Policy' : 'Terms of Use'}
      </Title>
      <Text c="dimmed" size="sm" mb="xl">
        Global Thematic Insights LLC — VoyLogic
      </Text>

      {isPrivacy ? <PrivacyContent /> : <TermsContent />}
    </Container>
  );
}

export default LegalPage;
